// Bridges the symmetry UI toggles + canvas bounds into a MirrorConfig, and
// expands a selection by its mirror reflections. The mirror MATH primitives
// (MirrorConfig / setMirror / mirrorCells) live in interaction/mirror; this is
// the app-state-aware layer on top (reads uiState.symmetry + the live cell
// bounds + grid occupancy), shared by drawing, selecting, and moving.

import { type MirrorConfig, mirrorCells, setMirror } from "../../interaction/mirror";
import { uiState } from "../../ui/state";
import { cellBounds } from "../../scene/work-volume-bounds";
import { grid } from "./grid";

// The current mirror config from the symmetry toggles + canvas bounds. Cells
// span [min, max-1], so a cell c on an axis pairs with (min + max - 1 - c) about
// the canvas centre.
export function currentMirrorConfig(): MirrorConfig {
  return {
    x: uiState.symmetry.x,
    y: uiState.symmetry.y,
    z: uiState.symmetry.z,
    sumX: cellBounds.minIx + cellBounds.maxIx - 1,
    sumY: cellBounds.minIy + cellBounds.maxIy - 1,
    sumZ: cellBounds.minIz + cellBounds.maxIz - 1,
  };
}

export function mirrorActiveNow(): boolean {
  return uiState.symmetry.x || uiState.symmetry.y || uiState.symmetry.z;
}

// Expand a set of selected cells by their mirror reflections, keeping only cells
// that are actually filled (the reflection of a voxel may be empty space). No-op
// when no mirror axis is on. Used so SELECTING with mirror grabs both sides.
export function mirrorExpandSelection(
  cells: Iterable<{ ix: number; iy: number; iz: number }>,
): { ix: number; iy: number; iz: number }[] {
  if (!mirrorActiveNow()) return [...cells];
  setMirror(currentMirrorConfig());
  const out: { ix: number; iy: number; iz: number }[] = [];
  const seen = new Set<string>();
  for (const c of cells) {
    for (const m of mirrorCells(c.ix, c.iy, c.iz)) {
      const k = `${m.ix},${m.iy},${m.iz}`;
      if (seen.has(k)) continue;
      seen.add(k);
      if (grid.has(m.ix, m.iy, m.iz)) out.push(m);
    }
  }
  return out;
}
